import type { DockPageRegistry } from "~/components/dock/dockPageRegistry";
import type { FusionMenuConfig } from "~/components/dock/FusionMenu";

import { ApplicationEnum } from "~/@types/enums";
import { type TranslateKey, translateWithActiveLanguage } from "~/helpers/localizedText";
import { colorForModule, iconForModule } from "~/helpers/recentDestinations";
import rankByLabel from "~/helpers/search/labelMatcher";
import i18n from "~/i18n/i18n";
import { resolveLocationMapLabel } from "~/layout/useModuleBranding";
import { flattenNavPageLinks, getFeaturePageMapping, systemPageMapping } from "~/locationMaps";
import createAdminDockPageRegistry from "~admin/dock/adminDockPageRegistry";
import buildAdminFusionConfig from "~admin/dock/adminFusionConfig";
import countRail from "~count/dock/countRail";
import createForecastDockPageRegistry from "~forecast/dock/forecastDockPageRegistry";
import createForecastFusionConfig from "~forecast/dock/forecastFusionConfig";
import createFulfillDockPageRegistry from "~fulfill/dock/fulfillDockPageRegistry";
import createFulfillFusionConfig from "~fulfill/dock/fulfillFusionConfig";
import growerBenchmarksRail from "~growerBenchmarks/dock/growerBenchmarksRail";
import intelligenceRail from "~intelligence/dock/intelligenceRail";
import buildAvailabilityRail from "~mobile/availability/dock/availabilityRail";
import buildEngageRail from "~mobile/engage/dock/engageRail";
import createProduceDockPageRegistry from "~produce/dock/produceDockPageRegistry";
import createProduceFusionConfig from "~produce/dock/produceFusionConfig";
import createReportsDockPageRegistry from "~reports/dock/reportsDockPageRegistry";
import createReportsFusionConfig from "~reports/dock/reportsFusionConfig";
import createRestockDockPageRegistry from "~restock/dock/restockDockPageRegistry";
import createRestockFusionConfig from "~restock/dock/restockFusionConfig";

export interface PageSearchEntry {
	breadcrumbModule?: string;
	color: string;
	icon: string;
	label: string;
	labelKey?: string;
	module: string;
	parentKey?: string;
	path: string;
}

const isNavigablePath = (path: string): boolean => /^\/[a-zA-Z0-9\-_/]*$/.test(path);

const moduleSegment = (path: string): string => path.split("/").filter(Boolean)[0] ?? "worksuite";

const dockTitleKeys = i18n.getFixedT("cimode", "common");
const railTitleKeys: TranslateKey = (key) => key;

const allRestock = {
	canAccessOrders: true,
	dashboardEnabled: true,
	kittingEnabled: true,
	ordersEnabled: true
};
const allAdmin = {
	canEnter: true,
	canManageCustomerNotifications: true,
	canManageDevices: true,
	canManageEmail: true,
	canManageSecurity: true,
	canManageUsers: true,
	canViewBilling: true,
	canViewGeneral: true,
	canViewIntegrations: true
};

const dashboardEnabled = true;
const canManageCommissions = true;
const kitsEnabled = true;
const logisticsEnabled = true;
const consolidationEnabled = false;
const forecastDefaultPage = "forecasts";
const showScopingUI = true;
const canManageParentCompany = true;
const canManageSecurity = true;
const canRecordShrink = true;
const planBuilderSearchable = false;
const costReportSearchable = false;
const productionCenterSearchable = false;

interface DockRegistry {
	config: FusionMenuConfig;
	model: DockPageRegistry;
}

const dockRegistries = (): DockRegistry[] => [
	{
		config: createFulfillFusionConfig(
			dockTitleKeys,
			dashboardEnabled,
			canManageCommissions,
			kitsEnabled,
			logisticsEnabled,
			[],
			false,
			false,
			true
		),
		model: createFulfillDockPageRegistry(dockTitleKeys, dashboardEnabled, kitsEnabled, logisticsEnabled)
	},
	{
		config: createProduceFusionConfig(
			dockTitleKeys,
			planBuilderSearchable,
			consolidationEnabled,
			costReportSearchable,
			true,
			productionCenterSearchable
		),
		model: createProduceDockPageRegistry(
			dockTitleKeys,
			planBuilderSearchable,
			consolidationEnabled,
			costReportSearchable,
			productionCenterSearchable
		)
	},
	{
		config: createRestockFusionConfig(dockTitleKeys, allRestock),
		model: createRestockDockPageRegistry(dockTitleKeys, allRestock, "dashboard")
	},
	{
		config: createForecastFusionConfig(dockTitleKeys),
		model: createForecastDockPageRegistry(dockTitleKeys, forecastDefaultPage, showScopingUI)
	},
	{
		config: createReportsFusionConfig(dockTitleKeys),
		model: createReportsDockPageRegistry(dockTitleKeys)
	},
	{
		config: buildAdminFusionConfig(allAdmin, dockTitleKeys, canManageParentCompany),
		model: createAdminDockPageRegistry(dockTitleKeys, canManageParentCompany, canManageSecurity)
	},
	countRail,
	intelligenceRail,
	growerBenchmarksRail,
	buildAvailabilityRail("/availability", canRecordShrink, railTitleKeys),
	buildEngageRail(railTitleKeys)
];

const buildDockPageEntries = (): PageSearchEntry[] =>
	dockRegistries().flatMap(({ config, model }) => {
		const items = config.sections.flatMap((section) =>
			section.groups.flatMap((group) => group.items.map((item) => ({ item, section })))
		);
		return Object.values(model.pagesById).map((page) => {
			const found = items.find(({ item }) => item.pageId === page.id);
			const module = moduleSegment(page.path);
			return {
				breadcrumbModule: config.module.toUpperCase(),
				color: colorForModule(module),
				icon: found?.item.icon ?? iconForModule(module),
				label: page.title,
				labelKey: page.title,
				module,
				parentKey: found && found.section.label !== page.title ? found.section.label : undefined,
				path: page.path
			};
		});
	});

const buildLinkEntries = (): PageSearchEntry[] =>
	[...Object.values(ApplicationEnum).map((application) => getFeaturePageMapping(application)), systemPageMapping]
		.flatMap((mapping) => flattenNavPageLinks(mapping))
		.map((link) => {
			const module = moduleSegment(link.path);
			return {
				color: colorForModule(module),
				icon: link.icon ?? iconForModule(module),
				label: link.label,
				labelKey: link.labelKey,
				module,
				path: link.path
			};
		});

const buildPageSearchIndex = (): PageSearchEntry[] => {
	const seen = new Set<string>();
	const entries: PageSearchEntry[] = [];
	for (const entry of [...buildLinkEntries(), ...buildDockPageEntries()]) {
		if (!isNavigablePath(entry.path) || seen.has(entry.path)) continue;
		seen.add(entry.path);
		entries.push(entry);
	}
	return entries;
};

let cachedIndex: PageSearchEntry[] | null = null;
const getPageSearchIndex = (): PageSearchEntry[] => (cachedIndex ??= buildPageSearchIndex());

const pageSearchLabel = (entry: PageSearchEntry, translate: TranslateKey = translateWithActiveLanguage): string => {
	const title = resolveLocationMapLabel(entry.path, entry.label, entry.labelKey, translate);
	if (!entry.breadcrumbModule) return title;
	const parent = entry.parentKey ? [translate(entry.parentKey)] : [];
	const moduleLabel = resolveLocationMapLabel(`/${entry.module}`, entry.breadcrumbModule, undefined, translate);
	return [moduleLabel, ...parent, title].join(" › ");
};

const searchPages = (
	query: string,
	limit: number,
	translate?: TranslateKey,
	index = getPageSearchIndex()
): PageSearchEntry[] =>
	rankByLabel(index, (entry) => pageSearchLabel(entry, translate), query, { limit }).map((match) => match.item);

export { buildDockPageEntries, buildPageSearchIndex, getPageSearchIndex, pageSearchLabel, searchPages };
export default searchPages;
